import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { IStorage, LocalStorage } from '@isophi/core-legacy';
import { IconService, ToastService } from '@isophi/core-ui';
import { ChildProfileDetail, ChildProfileDocumentRead, DocumentsService, PaginatedChildProfileDocumentReadList } from '@isophi/mng-api';
import { PopUpComponent } from '@isophi/teachers-shared';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, finalize, Observable, switchMap } from 'rxjs';

import { LocalStorageConstService } from '../../../../../../shared/services/local-storage-const.service';

@Component({
  selector: 'app-child-info-documents-tab',
  templateUrl: './child-info-documents-tab.component.html',
  styleUrls: ['./child-info-documents-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChildInfoDocumentsTabComponent implements OnInit {
  @Input()
  child: ChildProfileDetail;

  documents$: Observable<PaginatedChildProfileDocumentReadList>;

  page = 1;

  limit = 10;

  offset = 0;

  private documentsReload$ = new BehaviorSubject<void>(null);

  private readonly localStorage: IStorage = new LocalStorage();

  constructor(
    public iconsService: IconService,
    private documentsService: DocumentsService,
    private translocoService: TranslocoService,
    private popUp: PopUpComponent,
    private spinner: NgxSpinnerService,
    private toastr: ToastService,
    private lSConstService: LocalStorageConstService
  ) {}

  ngOnInit(): void {
    const savedLimit = this.localStorage.getItem(this.lSConstService.documentsPageLimit);
    if (savedLimit) {
      this.limit = +savedLimit;
    }
    this.initData();
  }

  initData(): void {
    this.documents$ = this.documentsReload$.pipe(
      switchMap(() => this.documentsService.documentsList(this.child.childId, this.limit, this.offset))
    );
  }

  reloadDocuments(): void {
    this.documentsReload$.next();
  }

  getIconMimeType(mimeType: string): string {
    return mimeType.split('/')[1];
  }

  onPageChange(page: number): void {
    this.offset = (page - 1) * this.limit;
    this.reloadDocuments();
  }

  onLimitChange(value: number): void {
    this.limit = value;
    this.page = 1;
    this.offset = 0;
    this.localStorage.setItem(this.lSConstService.documentsPageLimit, String(value));
    this.reloadDocuments();
  }

  addDocument(event): void {
    const file = event.target.files[0];

    if (file) {
      this.spinner.show();

      const reader = new FileReader();
      reader.readAsDataURL(file);

      this.documentsService
        .documentsCreateForm(file, file.name, this.child.childId)
        .pipe(finalize(() => this.spinner.hide()))
        .subscribe({
          next: (val) => {
            if (val) {
              this.toastr.success(
                this.translocoService.translate('child/uploadDocumentSuccess'),
                this.translocoService.translate('child/uploadDocument')
              );
              this.reloadDocuments();
            }
          },
          error: () => {
            this.toastr.error(
              this.translocoService.translate('child/uploadDocumentFailed'),
              this.translocoService.translate('child/uploadDocument')
            );
          },
        });
    }
  }

  async removeDocument(e: Event, document: ChildProfileDocumentRead): Promise<void> {
    e.stopPropagation();

    const title = this.translocoService.translate('child/removeDocument');
    const text = this.translocoService.translate('child/removeDocumentConfirm');
    const yesText = this.translocoService.translate('shared/YES');
    const noText = this.translocoService.translate('shared/NO');
    const confirm = await this.popUp.confirm(title, text, null, yesText, noText, 'btn-primary', 'btn-outline-primary');

    if (!confirm) return;

    await this.spinner.show();
    this.documentsService
      .documentsDestroy(document.id)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translocoService.translate('child/removeDocumentSuccess'),
            this.translocoService.translate('child/removeDocument')
          );
          this.reloadDocuments();
        },
        error: () => {
          this.toastr.error(
            this.translocoService.translate('child/removeDocumentFailed'),
            this.translocoService.translate('child/removeDocument')
          );
        },
      });
  }
}
